import Box from '@mui/material/Box';
import { useQuery } from '@tanstack/react-query';
import { clientService, projectService, reportsService, teamService } from 'api';
import { Download, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DateRangePicker } from 'shared/components/DateRangePicker';
import { HeroContainer } from 'shared/components/HeroContainer';
import { queryKeys } from 'shared/constants/queryKeys';
import { useAppConfig } from 'shared/hooks/useAppConfig';
import { usePermissions } from 'shared/hooks/usePermissions';

import {
  ContentSection,
  ReportsContainer,
  ReportsHeroActions,
  AddEntryButton,
  ReportsHeroExportButton,
  ReportsHeroRow,
  ReportsHeroStatCard,
  ReportsHeroStatCards,
  ReportsHeroStatLabel,
  ReportsHeroStatSub,
  ReportsHeroStatValue,
  ReportsHeroSubtitle,
  ReportsHeroTextBox,
  ReportsHeroTitle,
  TabContentWrapper,
  TabLabel,
  TabsWrapper,
  TabsHeader,
} from '../reports.styles';
import type { ReportFiltersState, ReportTab } from '../types/reports.types';
import {
  formatDuration,
  getDefaultStartDate,
  getDefaultEndDate,
  getWeekForDate,
} from '../utils/reportsUtils';

import { DetailedTab } from './DetailedTab';
import { ReportFiltersBar } from './ReportFiltersBar';
import SmartReportWidget from './SmartReportWidget';
import { SummaryTab } from './SummaryTab';
import { WeeklyTab } from './WeeklyTab';

const VALID_TABS: ReportTab[] = ['summary', 'detailed', 'weekly', 'shared'];

const ReportsPage: React.FC = () => {
  const { smartReportEnabled } = useAppConfig();
  const { canCreateEntriesForOthers } = usePermissions();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<ReportTab>('summary');
  const [filters, setFilters] = useState<ReportFiltersState>({});
  const [appliedFilters, setAppliedFilters] = useState<ReportFiltersState>({});
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [exportAnchorEl, setExportAnchorEl] = useState<HTMLElement | null>(null);
  const [detailedAddEntryOpen, setDetailedAddEntryOpen] = useState(false);

  // Seed state from URL params on mount (e.g. when opened from an EntityLink)
  useEffect(() => {
    const tab = searchParams.get('tab') as ReportTab | null;
    const projectIds = searchParams.get('projectIds')?.split(',').filter(Boolean);
    const clientIds = searchParams.get('clientIds')?.split(',').filter(Boolean);
    const userIds = searchParams.get('userIds')?.split(',').filter(Boolean);
    const start = searchParams.get('start');
    const end = searchParams.get('end');

    const hasFilters = projectIds?.length || clientIds?.length || userIds?.length;
    if (!tab && !hasFilters && !start && !end) return;

    if (tab && VALID_TABS.includes(tab)) {
      setActiveTab(tab);
    }
    if (hasFilters) {
      const seedFilters: ReportFiltersState = {
        ...(projectIds?.length ? { projectIds } : {}),
        ...(clientIds?.length ? { clientIds } : {}),
        ...(userIds?.length ? { userIds } : {}),
      };
      setFilters(seedFilters);
      setAppliedFilters(seedFilters);
    }
    if (start) setStartDate(start);
    if (end) setEndDate(end);
    // Run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch clients and projects
  const { data: clients = [] } = useQuery({
    queryKey: queryKeys.clients.list(),
    queryFn: () => clientService.getClients().then((res) => res.data.data),
  });

  const { data: projects = [] } = useQuery({
    queryKey: queryKeys.projects.list(),
    queryFn: () => projectService.getProjects().then((res) => res.data.data),
  });

  const { data: teamMembers = [] } = useQuery({
    queryKey: queryKeys.team.members(),
    queryFn: () => teamService.getMembers().then((res) => res.data.data),
  });

  const handleFiltersChange = (newFilters: ReportFiltersState) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = (newFilters: ReportFiltersState) => {
    setAppliedFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({});
    setAppliedFilters({});
    setStartDate(getDefaultStartDate());
    setEndDate(getDefaultEndDate());
  };

  // Passed to DateRangePicker as onDateClick override for weekly tab (snaps to full week)
  const handleDateClick = (clickedDate: string) => {
    if (activeTab === 'weekly') {
      const range = getWeekForDate(clickedDate);
      setStartDate(range.start);
      setEndDate(range.end);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const newTab = ['summary', 'detailed', 'weekly', 'shared'][newValue] as ReportTab;
    setActiveTab(newTab);
    if (newTab === 'weekly') {
      const range = getWeekForDate(startDate);
      setStartDate(range.start);
      setEndDate(range.end);
    }
  };

  // Summary totals for hero stat cards — same query key as SummaryTab so React Query deduplicates
  const heroSummaryParams = {
    from: startDate,
    to: endDate,
    userIds: appliedFilters.userIds?.join(','),
    projectIds: appliedFilters.projectIds?.join(','),
    clientIds: appliedFilters.clientIds?.join(','),
  };
  const { data: heroSummary } = useQuery({
    queryKey: queryKeys.reports.summary(heroSummaryParams),
    queryFn: async () => {
      const res = await reportsService.getSummary(heroSummaryParams);
      return res.data.data;
    },
    enabled: !!startDate,
  });

  const clientOptions = clients.map((c: any) => ({
    id: c.id,
    name: c.name,
    isArchived: c.isArchived,
  }));
  const projectOptions = projects.map((p: any) => ({
    id: p.id,
    name: p.name,
    color: p.color,
    isArchived: p.isArchived,
    isBillable: p.isBillable,
  }));
  const teamOptions = teamMembers.map((m: any) => ({
    id: m.userId,
    name: m.name || m.email,
    status: m.status,
  }));

  return (
    <ReportsContainer>
      {/* Hero banner */}
      <HeroContainer padding="24px 28px" style={{ marginBottom: 20 }}>
        <ReportsHeroRow>
          <ReportsHeroTextBox>
            <ReportsHeroTitle>Reports</ReportsHeroTitle>
            <ReportsHeroSubtitle>
              Analyze productivity, billable hours, and team performance.
            </ReportsHeroSubtitle>
          </ReportsHeroTextBox>
          <ReportsHeroActions>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }}
              shortcuts={
                activeTab === 'weekly'
                  ? ['thisWeek', 'lastWeek']
                  : [
                      'today',
                      'yesterday',
                      'thisWeek',
                      'lastWeek',
                      'lastTwoWeeks',
                      'thisMonth',
                      'lastMonth',
                      'thisYear',
                      'lastYear',
                    ]
              }
              onDateClick={activeTab === 'weekly' ? handleDateClick : undefined}
              heromode
            />
            {activeTab === 'detailed' && canCreateEntriesForOthers && (
              <AddEntryButton
                variant="contained"
                size="small"
                disableElevation
                onClick={() => setDetailedAddEntryOpen(true)}
              >
                <Plus size={14} />
                Add Entry
              </AddEntryButton>
            )}
            <ReportsHeroExportButton
              variant="outlined"
              size="small"
              disableElevation
              onClick={(e) => setExportAnchorEl(e.currentTarget)}
            >
              <Download size={14} />
              Export
            </ReportsHeroExportButton>
          </ReportsHeroActions>
        </ReportsHeroRow>

        {/* Stat cards */}
        <ReportsHeroStatCards>
          <ReportsHeroStatCard>
            <ReportsHeroStatLabel>Total</ReportsHeroStatLabel>
            <ReportsHeroStatValue>
              {formatDuration(heroSummary?.totalDurationSecs ?? 0)}
            </ReportsHeroStatValue>
            {/* <ReportsHeroStatSub>
              {heroSummary?.groups?.length ?? 0} entries
            </ReportsHeroStatSub> */}
          </ReportsHeroStatCard>
          <ReportsHeroStatCard>
            <ReportsHeroStatLabel>Billable</ReportsHeroStatLabel>
            <ReportsHeroStatValue>
              {formatDuration(heroSummary?.billableDurationSecs ?? 0)}
            </ReportsHeroStatValue>
            <ReportsHeroStatSub>
              {heroSummary
                ? Math.round(
                    ((heroSummary.billableDurationSecs ?? 0) /
                      Math.max(heroSummary.totalDurationSecs ?? 1, 1)) *
                      100,
                  )
                : 0}
              % of total
            </ReportsHeroStatSub>
          </ReportsHeroStatCard>
        </ReportsHeroStatCards>
      </HeroContainer>

      {/* Tabs Header */}
      <TabsHeader>
        <TabsWrapper
          value={['summary', 'detailed', 'weekly', 'shared'].indexOf(activeTab)}
          onChange={handleTabChange}
        >
          <TabLabel label="Summary" />
          <TabLabel label="Detailed" />
          <TabLabel label="Weekly" />
          {/* To do later */}
          {/* <TabLabel label="Shared" /> */}
        </TabsWrapper>
      </TabsHeader>

      {/* Filters Section */}
      <ReportFiltersBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onApply={(f) => handleApplyFilters(f)}
        onClearFilters={handleClearFilters}
        clients={clientOptions}
        projects={projectOptions}
        team={teamOptions}
      />

      {/* Smart Report (AI work-log quality) summary + entry point */}
      {smartReportEnabled && (
        <SmartReportWidget startDate={startDate} endDate={endDate} filters={appliedFilters} />
      )}

      {/* Tab Content */}
      <ContentSection>
        <TabContentWrapper>
          {activeTab === 'summary' && (
            <SummaryTab
              startDate={startDate}
              endDate={endDate}
              filters={appliedFilters}
              projects={projects}
              clients={clients}
              users={teamOptions}
              exportAnchorEl={exportAnchorEl}
              onExportMenuClose={() => setExportAnchorEl(null)}
            />
          )}
          {activeTab === 'detailed' && (
            <DetailedTab
              startDate={startDate}
              endDate={endDate}
              filters={appliedFilters}
              projects={projects}
              clients={clients}
              exportAnchorEl={exportAnchorEl}
              onExportMenuClose={() => setExportAnchorEl(null)}
              addEntryOpen={detailedAddEntryOpen}
              onAddEntryClose={() => setDetailedAddEntryOpen(false)}
            />
          )}
          {activeTab === 'weekly' && (
            <WeeklyTab
              startDate={startDate}
              endDate={endDate}
              filters={appliedFilters}
              projects={projects}
              clients={clients}
              users={teamOptions}
              exportAnchorEl={exportAnchorEl}
              onExportMenuClose={() => setExportAnchorEl(null)}
            />
          )}
          {activeTab === 'shared' && <Box>{/* SharedReport component will go here */}</Box>}
        </TabContentWrapper>
      </ContentSection>
    </ReportsContainer>
  );
};

export default ReportsPage;
